import PropTypes from 'prop-types'
import {
  Text,
  Flex,
  Button,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import {useRails} from '../../../contexts/rails'
import {fetcher, defaultHeaders} from '../../../../api'
import DropdownSelect from '../../DropdownSelect'
import {BankAccountCard} from '../components'

const validationSchema = Yup.object().shape({
  reconciliationAccount: Yup.object()
    .nullable()
    .required('Please select an account'),
})

const ReconciliationAccountScreen = ({
  goToNextStep,
  onClose,
  connectedPayoutAccount,
}) => {
  const {t} = useTranslation('connectPayoutAccount')
  const {organisation, accountingProvider, authenticityToken} = useRails()
  const supplierId = organisation.currentSupplier.id

  const BANK_ACCOUNTS_ENDPOINT = `/api/suppliers/${supplierId}/bank_accounts`
  const RECONCILIATION_ACCOUNT_ENDPOINT = `/api/suppliers/${supplierId}/reconciliation_account`

  const {data, isLoading, error} = useSWR(BANK_ACCOUNTS_ENDPOINT, fetcher)

  const accountLabel = (account) => (
    <Flex flexDirection="column">
      <Text fontSize="sm">{account.Name}</Text>
      {account.BankAccountNumber && (
        <Text fontSize="xs" color="mld.neutral.700">
          {account.BankAccountNumber}
        </Text>
      )}
    </Flex>
  )

  const options =
    data?.accounts?.map((account) => ({
      label: accountLabel(account),
      value: account.AccountID,
    })) ?? []

  const saveReconciliationAccount = async (url, {arg}) => {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: defaultHeaders({authenticityToken}),
      body: JSON.stringify({supplier: {bank_account_id: arg}}),
    })

    const responseData = await response.json()

    if (!response.ok) {
      const saveError = new Error(
        responseData?.error?.message || 'Failed to save reconciliation account',
      )
      saveError.info = responseData
      saveError.status = response.status
      throw saveError
    }

    return responseData
  }

  const {trigger, isMutating} = useSWRMutation(
    RECONCILIATION_ACCOUNT_ENDPOINT,
    saveReconciliationAccount,
  )

  const formik = useFormik({
    initialValues: {reconciliationAccount: null},
    validationSchema,
    onSubmit: async (values) => {
      await trigger(values.reconciliationAccount.value)
      await goToNextStep()
    },
  })

  return (
    <Flex justify="center">
      <Flex flexDirection="column" width="400px" gap={5} pt={10}>
        <Text
          color="black"
          fontWeight="regular"
          fontSize="2xl"
          lineHeight="90%"
        >
          {t('modal.reconciliationAccount.title')}
        </Text>

        <Text color="black" fontSize="14px" lineHeight="125%">
          {t('modal.reconciliationAccount.description')}
        </Text>

        {connectedPayoutAccount && (
          <Flex flexDirection="column" gap={2}>
            <Text fontSize="sm" fontWeight="medium">
              {t('modal.reconciliationAccount.payoutAccountLabel')}
            </Text>
            <BankAccountCard
              accountName={connectedPayoutAccount.bank_account_name}
              accountNumber={connectedPayoutAccount.account_number}
              isDisplay
            />
          </Flex>
        )}

        <FormControl
          isInvalid={
            formik.touched.reconciliationAccount &&
            !!formik.errors.reconciliationAccount
          }
        >
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            {t('modal.reconciliationAccount.selectLabel', {
              accounting_provider: accountingProvider.name,
            })}
          </Text>
          {error ? (
            <Text fontSize="sm" color="mld.alert.700">
              {t('modal.reconciliationAccount.fetchError')}
            </Text>
          ) : (
            <DropdownSelect
              name="reconciliation-account"
              placeholder={t('modal.buttons.selectAnAccount')}
              options={options}
              value={formik.values.reconciliationAccount}
              onChange={(option) =>
                formik.setFieldValue('reconciliationAccount', option)
              }
              onBlur={() => formik.setFieldTouched('reconciliationAccount')}
              isDisabled={isLoading}
              isLoading={isLoading}
              variant="mld"
            />
          )}
          <FormErrorMessage>
            {formik.errors.reconciliationAccount}
          </FormErrorMessage>
        </FormControl>

        <Flex gap="sm">
          <Button
            variant="primary"
            size="md"
            onClick={formik.handleSubmit}
            isLoading={isMutating || formik.isSubmitting}
            isDisabled={Boolean(error)}
            loadingText={t('modal.buttons.continue')}
          >
            {t('modal.buttons.continue')}
          </Button>
          <Button
            borderRadius="full"
            variant="secondary"
            width="120px"
            onClick={onClose}
            _hover={{}}
          >
            {t('modal.buttons.cancel')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

ReconciliationAccountScreen.propTypes = {
  goToNextStep: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  connectedPayoutAccount: PropTypes.shape({
    bank_account_name: PropTypes.string,
    account_number: PropTypes.string,
  }).isRequired,
}

export default ReconciliationAccountScreen
